/**
 * Lightweight JWT session storage for the Google OAuth path.
 *
 * The backend's /api/oauth/google/callback redirects to
 *   ${FRONTEND_URL}/auth/callback#auth=success&token=<JWT>
 * AuthCallbackPage hands the token to setSession() and then navigates
 * the user into the protected shell. The token rides in the fragment, not
 * the query string, because a query string reaches nginx and is archived
 * in its access log for 52 days; a fragment is never sent to a server.
 *
 * Token lives in localStorage so it survives reloads. Auth0's SPA
 * provider keeps tokens in memory; we use storage here because the
 * backend doesn't issue refresh tokens — losing the JWT on refresh
 * would log everyone out.
 *
 * Auth0 (when wired) takes precedence over this path; the SDK's
 * isAuthenticated drives ProtectedRoute first, this is the fallback.
 */

const STORAGE_KEY = 'cc.auth.token';

export interface SessionTokenClaims {
  /** The user id on the backend's tokens (services/auth/tokenService.js). */
  id?: string;
  userId?: string;
  sub?: string;
  email?: string;
  name?: string;
  exp?: number;
}

function decodeClaims(token: string): SessionTokenClaims | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as SessionTokenClaims;
  } catch {
    return null;
  }
}

export function setSession(token: string): void {
  if (!token) return;
  try {
    localStorage.setItem(STORAGE_KEY, token);
  } catch {
    // localStorage disabled (private mode) — tab-scoped fallback
    sessionStorage.setItem(STORAGE_KEY, token);
  }
}

export function getSessionToken(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Drop the local session and send the browser to /login. `reason` rides in
 * the query string so the sign-in page can say why the console emptied.
 *
 * A full navigation (not a router push) on purpose: it discards every
 * in-memory cache (TanStack Query, Cerebrix drawer state) that belonged to
 * the session that just ended, so nothing from it can be painted for the
 * next person who signs in on this browser.
 *
 * `expired` — the bearer stopped being accepted (401).
 * `deactivated` — the account behind it was deactivated (403 ACCOUNT_DEACTIVATED).
 */
export type SessionEndReason = 'expired' | 'deactivated';

export function endSession(reason?: SessionEndReason): void {
  clearSession();
  window.location.assign(reason ? `/login?reason=${reason}` : '/login');
}

export function getSessionClaims(): SessionTokenClaims | null {
  const token = getSessionToken();
  return token ? decodeClaims(token) : null;
}

/** The signed-in user's id from the session token; null when there is none. */
export function getSessionUserId(): string | null {
  const claims = getSessionClaims();
  const id = claims?.id ?? claims?.userId ?? claims?.sub;
  return typeof id === 'string' && id.length > 0 ? id : null;
}

/**
 * Returns true when the session token exists and the JWT exp claim
 * is in the future. Tokens without an exp claim are treated as valid;
 * the API will reject them at request time.
 */
export function hasSession(): boolean {
  const token = getSessionToken();
  if (!token) return false;
  const claims = decodeClaims(token);
  if (claims?.exp && claims.exp * 1000 < Date.now()) {
    clearSession();
    return false;
  }
  return true;
}
